/* Northtac theme — product.js (variant picker, sticky ATC, media) */

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.productJsonEl = document.getElementById(`ProductJSON-${this.sectionId}`);
    if (!this.productJsonEl) return;
    this.product = JSON.parse(this.productJsonEl.textContent);
    this.addEventListener('change', this.onVariantChange.bind(this));
    // Mark sold-out options up front — shoppers should never have to click
    // an option to find out it is gone.
    this.options = this.getSelectedOptions();
    this.currentVariant = this.product.variants.find((variant) =>
      variant.options.every((option, index) => option === this.options[index])
    );
    this.updateOptionAvailability();
    this.updateQuantityMax();
    this.updateLowStock();
  }

  onVariantChange() {
    this.options = this.getSelectedOptions();
    this.currentVariant = this.product.variants.find((variant) =>
      variant.options.every((option, index) => option === this.options[index])
    );
    this.updateHiddenInput();
    this.updatePrice();
    this.updateAvailability();
    this.updateOptionAvailability();
    this.updateQuantityMax();
    this.updateLowStock();
    this.updateURL();
    this.updateStickyBar();
    this.updateMedia();
    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: this.currentVariant }
      })
    );
  }

  getSelectedOptions() {
    const selects = Array.from(this.querySelectorAll('select[data-option-index]'));
    if (selects.length) return selects.map((s) => s.value);
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    return fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  updateHiddenInput() {
    // Includes the dynamic-checkout form's id input: the wallet button
    // serializes its own form at click time, so it must follow the
    // selected variant too (it used to keep the first variant's id).
    document.querySelectorAll(`#product-form-${this.sectionId} input[name="id"], #sticky-atc-form-${this.sectionId} input[name="id"], [data-dynamic-checkout="${this.sectionId}"] input[name="id"]`).forEach((input) => {
      if (this.currentVariant) input.value = this.currentVariant.id;
    });
    // Installments banner: Shopify's shopify-payment-terms component watches
    // its form's id input for change events and re-renders the real plan.
    const installments = document.querySelector(`[data-installments-input="${this.sectionId}"]`);
    if (installments && this.currentVariant) {
      installments.value = this.currentVariant.id;
      installments.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  formatMoney(cents) {
    // Always two decimals: the server-rendered price uses Shopify's money
    // format ($449.00), and a format change on variant switch reads as a
    // price change to the shopper.
    return `$${(cents / 100).toFixed(2)}`;
  }

  updatePrice() {
    const priceEls = document.querySelectorAll(`[data-price-wrapper="${this.sectionId}"]`);
    if (!this.currentVariant) return;
    priceEls.forEach((el) => {
      const sale = el.querySelector('[data-price]');
      const compare = el.querySelector('[data-compare-price]');
      if (sale) sale.textContent = this.formatMoney(this.currentVariant.price);
      if (compare) {
        const has = this.currentVariant.compare_at_price > this.currentVariant.price;
        compare.toggleAttribute('hidden', !has);
        if (has) compare.textContent = this.formatMoney(this.currentVariant.compare_at_price);
      }
    });
  }

  updateAvailability() {
    const buttons = document.querySelectorAll(`#product-form-${this.sectionId} [type="submit"], #sticky-atc-form-${this.sectionId} [type="submit"]`);
    const statusEls = document.querySelectorAll(`[data-availability="${this.sectionId}"]`);
    let label;
    let disabled = false;
    if (!this.currentVariant) {
      label = window.variantStrings.unavailable;
      disabled = true;
    } else if (!this.currentVariant.available) {
      label = window.variantStrings.soldOut;
      disabled = true;
    } else {
      label = window.variantStrings.addToCart;
    }
    buttons.forEach((button) => {
      const text = button.querySelector('span');
      if (text) text.textContent = label;
      if (disabled) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      button.toggleAttribute('disabled', disabled);
    });
    statusEls.forEach((el) => {
      const available = this.currentVariant && this.currentVariant.available;
      el.classList.toggle('is-unavailable', !available);
      const textEl = el.querySelector('[data-availability-text]');
      if (textEl) textEl.textContent = available ? el.dataset.textAvailable : el.dataset.textUnavailable;
    });
    // Dynamic checkout (Buy with Shop / PayPal) is hidden entirely for
    // sold-out variants — an express button you can click on a sold-out
    // product is a broken promise, not a CTA.
    document.querySelectorAll(`[data-dynamic-checkout="${this.sectionId}"]`).forEach((el) => {
      el.toggleAttribute('hidden', disabled);
    });
  }

  /* For every option value, check whether ANY purchasable variant exists with
     that value while keeping the OTHER currently selected options fixed —
     that is exactly the variant the shopper would land on by clicking it. */
  updateOptionAvailability() {
    const selected = this.getSelectedOptions();
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach((fieldset, index) => {
      fieldset.querySelectorAll('.variant-option input').forEach((input) => {
        const candidate = selected.slice();
        candidate[index] = input.value;
        const variant = this.product.variants.find((v) =>
          v.options.every((option, i) => option === candidate[i])
        );
        const available = !!(variant && variant.available);
        const card = fieldset.querySelector(`label[for="${input.id}"]`);
        if (card) card.classList.toggle('is-unavailable', !available);
      });
    });
    Array.from(this.querySelectorAll('select[data-option-index]')).forEach((select, index) => {
      Array.from(select.options).forEach((opt) => {
        const candidate = selected.slice();
        candidate[index] = opt.value;
        const variant = this.product.variants.find((v) =>
          v.options.every((option, i) => option === candidate[i])
        );
        const available = !!(variant && variant.available);
        const base = opt.value;
        opt.textContent = available ? base : `${base} — Sold out`;
      });
    });
  }

  /* Keep the PDP quantity input's max in step with the selected variant's
     stock (tracked, no-oversell variants only) so + stops at the limit. */
  updateQuantityMax() {
    const input = document.querySelector(`#product-form-${this.sectionId} .quantity__input`);
    if (!input || !this.currentVariant) return;
    const v = this.currentVariant;
    if (v.inventory_management && v.inventory_policy === 'deny') {
      const max = Math.max(1, v.inventory_quantity || 0);
      input.max = max;
      if (parseInt(input.value, 10) > max) input.value = max;
    } else {
      input.removeAttribute('max');
    }
    const qi = input.closest('quantity-input');
    if (qi && typeof qi.syncMax === 'function') qi.syncMax();
  }

  /* Low-stock urgency line ("Low stock — only N left"): follows the selected
     variant. Only for tracked, no-oversell variants under the threshold. */
  updateLowStock() {
    const el = document.querySelector(`[data-low-stock="${this.sectionId}"]`);
    if (!el) return;
    const v = this.currentVariant;
    const threshold = parseInt(el.dataset.threshold, 10) || 5;
    const show = !!(
      v &&
      v.available &&
      v.inventory_management &&
      v.inventory_policy === 'deny' &&
      v.inventory_quantity > 0 &&
      v.inventory_quantity <= threshold
    );
    el.toggleAttribute('hidden', !show);
    if (show) {
      const textEl = el.querySelector('[data-low-stock-text]');
      if (textEl) textEl.textContent = (el.dataset.text || '').replace('[count]', v.inventory_quantity);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateStickyBar() {
    const bar = document.querySelector(`[data-sticky-atc="${this.sectionId}"]`);
    if (!bar || !this.currentVariant) return;
    // Keep the bar's variant select in step with the main picker.
    const variantSelect = bar.querySelector('[data-sticky-variant]');
    if (variantSelect) variantSelect.value = String(this.currentVariant.id);
    const variantLabel = bar.querySelector('.sticky-atc__variant');
    if (variantLabel && this.currentVariant.title !== 'Default Title') {
      variantLabel.textContent = this.currentVariant.title;
    }
    const price = bar.querySelector('.sticky-atc__price');
    if (price) price.textContent = this.formatMoney(this.currentVariant.price);
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const target = document.querySelector(
      `[data-media-id="${this.sectionId}-${this.currentVariant.featured_media.id}"]`
    );
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }
}
if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

/* ---------- Sticky-bar variant select ---------- */
/*
 * Changing the select drives the MAIN variant picker and fires one change
 * through it, so price, availability, quantity max, low stock, URL, media
 * and the wallet form all update through the single existing code path.
 * If the page has no picker (block removed), fall back to updating the
 * sticky form's id directly.
 */
document.addEventListener('change', (event) => {
  const select = event.target.closest?.('[data-sticky-variant]');
  if (!select) return;
  const sectionId = select.dataset.stickyVariant;
  const picker = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
  const variantId = select.value;

  if (picker && picker.product) {
    const variant = picker.product.variants.find((v) => String(v.id) === variantId);
    if (!variant) return;
    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    variant.options.forEach((value, i) => {
      if (fieldsets[i]) {
        const input = Array.from(fieldsets[i].querySelectorAll('.variant-option input')).find(
          (radio) => radio.value === value
        );
        if (input) input.checked = true;
      }
      const optSelect = picker.querySelector(`select[data-option-index="${i}"]`);
      if (optSelect) optSelect.value = value;
    });
    // One synthetic change on the picker runs the whole update pipeline.
    picker.dispatchEvent(new Event('change'));
  } else {
    document.querySelectorAll(`#sticky-atc-form-${sectionId} input[name="id"]`).forEach((input) => {
      input.value = variantId;
    });
  }
});

/* ---------- Subscription selling plan sync ---------- */
/*
 * Shopify's Subscriptions app renders its purchase-option widget as an app
 * block. This custom product section owns separate main, dynamic-checkout and
 * sticky forms, so mirror the selected plan into every form. Disabled inputs
 * are omitted for one-time purchases.
 */
function syncSellingPlanInputs(sectionId, planId) {
  document.querySelectorAll(`[data-selling-plan-input="${sectionId}"]`).forEach((input) => {
    input.value = planId || '';
    input.disabled = !planId;
  });
}

document.addEventListener('change', (event) => {
  const radio = event.target.closest?.(
    '.shopify_subscriptions_app_block input[type="radio"]'
  );
  if (!radio) return;

  const productInfo = radio.closest('[id^="ProductInfo-"]');
  if (!productInfo) return;

  const sectionId = productInfo.id.replace('ProductInfo-', '');
  const selectedPlan = productInfo.querySelector(
    '.shopify_subscriptions_app_block input[data-selling-plan-id]:checked'
  );
  const planId =
    radio.dataset.radioType === 'one_time_purchase'
      ? ''
      : selectedPlan?.dataset.sellingPlanId || radio.dataset.sellingPlanId || '';

  syncSellingPlanInputs(sectionId, planId);
});

document.addEventListener('variant:change', (event) => {
  const productInfo = event.target.closest?.('[id^="ProductInfo-"]');
  if (!productInfo) return;

  const sectionId = productInfo.id.replace('ProductInfo-', '');
  const selectedPlan = productInfo.querySelector(
    '.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) input[data-selling-plan-id]:checked'
  );
  syncSellingPlanInputs(sectionId, selectedPlan?.dataset.sellingPlanId || '');
});

/* ---------- Sticky add to cart ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.anchorSelector = this.dataset.anchor;
    this.anchor = document.querySelector(this.anchorSelector);
    if (!this.anchor || !('IntersectionObserver' in window)) return;
    this.bar = this.querySelector('.sticky-atc');
    this.observer = new IntersectionObserver(
      ([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        this.bar.classList.toggle('is-visible', !entry.isIntersecting && below);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.anchor);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

/* ---------- Sticky bar <-> Notify Me (back-in-stock app) ---------- */
/*
 * When the selected variant is sold out and the Notify Me app's button is
 * on the page, the sticky bar's dead "Sold out" button becomes a live
 * "Notify me" button that opens the app's dialog (by proxy-clicking the
 * app's own button, so the app keeps full control of its flow). Reverts
 * to the plain disabled button when the variant is available or the app
 * is absent. updateAvailability() rewrites the button's disabled state
 * and label on every variant change BEFORE variant:change fires, so the
 * disabled attribute read here always reflects true availability.
 */
const syncStickyNotifyButtons = () => {
  const appButton = document.querySelector('.nm-portal .restock-alerts-notify-button');
  document.querySelectorAll('[id^="sticky-atc-form-"] button[type="submit"]').forEach((button) => {
    if (appButton && button.disabled) {
      button.dataset.notifyProxy = '1';
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      const text = button.querySelector('span');
      if (text) text.textContent = 'Notify me';
    } else if (!button.disabled) {
      delete button.dataset.notifyProxy;
    }
  });
};

document.addEventListener('variant:change', () => syncStickyNotifyButtons());

document.addEventListener('click', (event) => {
  const proxy = event.target.closest?.('[data-notify-proxy]');
  if (!proxy) return;
  const appButton = document.querySelector('.nm-portal .restock-alerts-notify-button');
  if (!appButton) return;
  event.preventDefault();
  appButton.click();
});

/* The app injects its button asynchronously after page load — watch for it
   once, convert the sticky button, then stop observing. */
(() => {
  const tryInit = () => {
    if (!document.querySelector('.nm-portal .restock-alerts-notify-button')) return false;
    syncStickyNotifyButtons();
    return true;
  };
  if (tryInit()) return;
  const observer = new MutationObserver(() => {
    if (tryInit()) observer.disconnect();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ---------- Product media lightbox ---------- */
/*
 * Click any PDP gallery image ([data-zoom-src], rendered by main-product)
 * to open it large in an overlay. One overlay is built lazily and reused.
 * Arrows / arrow keys navigate, Esc / backdrop / X close, and clicking the
 * large image toggles a 1:1 zoom inside a scrollable stage.
 */
(() => {
  let lightbox = null;
  let images = [];
  let index = 0;
  let lastTrigger = null;

  const icon = (d) =>
    `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="${d}"/></svg>`;

  const pad = (n) => String(n).padStart(2, '0');

  const show = (next) => {
    index = (next + images.length) % images.length;
    const img = lightbox.querySelector('.product-lightbox__image');
    img.src = images[index].src;
    img.alt = images[index].alt;
    lightbox.classList.remove('is-zoomed');
    lightbox.querySelector('.product-lightbox__counter').textContent = `${pad(index + 1)} / ${pad(images.length)}`;
  };

  const onKey = (event) => {
    if (event.key === 'Escape') close();
    else if (event.key === 'ArrowLeft') show(index - 1);
    else if (event.key === 'ArrowRight') show(index + 1);
  };

  const close = () => {
    lightbox.hidden = true;
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    if (lastTrigger) lastTrigger.focus({ preventScroll: true });
  };

  const build = () => {
    lightbox = document.createElement('div');
    lightbox.className = 'product-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Product image gallery');
    lightbox.hidden = true;
    lightbox.innerHTML =
      '<div class="product-lightbox__stage"><img class="product-lightbox__image" alt=""></div>' +
      `<button type="button" class="product-lightbox__close" aria-label="Close">${icon('M2 2l12 12M14 2L2 14')}</button>` +
      `<button type="button" class="product-lightbox__nav product-lightbox__nav--prev" aria-label="Previous image">${icon('M10 2L4 8l6 6')}</button>` +
      `<button type="button" class="product-lightbox__nav product-lightbox__nav--next" aria-label="Next image">${icon('M6 2l6 6-6 6')}</button>` +
      '<span class="product-lightbox__counter"></span>';
    document.body.appendChild(lightbox);
    lightbox.querySelector('.product-lightbox__close').addEventListener('click', close);
    lightbox.querySelector('.product-lightbox__nav--prev').addEventListener('click', () => show(index - 1));
    lightbox.querySelector('.product-lightbox__nav--next').addEventListener('click', () => show(index + 1));
    lightbox.addEventListener('click', (event) => {
      if (event.target.closest('button')) return;
      if (event.target.classList.contains('product-lightbox__image')) {
        lightbox.classList.toggle('is-zoomed');
        if (lightbox.classList.contains('is-zoomed')) {
          const stage = lightbox.querySelector('.product-lightbox__stage');
          stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
          stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
        }
      } else {
        close();
      }
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-zoom-src]');
    if (!trigger) return;
    const scope = trigger.closest('.product') || document;
    const triggers = Array.from(scope.querySelectorAll('[data-zoom-src]'));
    images = triggers.map((el) => ({ src: el.dataset.zoomSrc, alt: el.alt || '' }));
    if (!images.length) return;
    if (!lightbox) build();
    const single = images.length < 2;
    lightbox.querySelectorAll('.product-lightbox__nav').forEach((b) => { b.hidden = single; });
    lightbox.querySelector('.product-lightbox__counter').hidden = single;
    lastTrigger = trigger;
    lightbox.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    show(Math.max(0, triggers.indexOf(trigger)));
    lightbox.querySelector('.product-lightbox__close').focus({ preventScroll: true });
  });
})();
